import React from "react";
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { HashLoader } from "react-spinners";

import { COLORS } from "../../utils/colors";
import Status from "../../components/TicketStatus";
import timeAgo from "../../utils/timeAgo";
import { getLogs } from "../../api/logs";
import { dateForHumans } from "../../utils/dateForHumans";

const columns = [
  { headerName: "ID", field: "id", flex: 1 },
  {
    headerName: "Status",
    field: "status",
    flex: 1,
    renderCell: (params) => <Status status={params.value} />,
  },
  { headerName: "Endpoint", field: "endpoint", flex: 1 },
  { headerName: "Api Key", field: "api_key", flex: 1 },
  { headerName: "When", field: "date", flex: 1 },
];

export default function Logs({ userData, handleSnackbar }) {
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [log, setLog] = useState({});
  const [logsObject, setLogsObject] = useState(null);

  useEffect(async () => {
    setLoading(true);

    try {
      if (userData) {
        const response = await getLogs({
          limit: 5000,
          apiKey: userData.operators[0].api_key,
        });

        if (response.error) {
          handleSnackbar(response.error.message, "error");
        }

        if (response.data) {
          processDataGridData(response.data);
        }
      }
    } catch (error) {
      handleSnackbar(error.message, "error");
    }
    setLoading(false);
  }, []);

  const toggleDrawer = () => {
    setOpenDrawer(!openDrawer);
  };

  const processDataGridData = (data) => {
    const array = [];

    data.forEach((log, index) => {
      const customerData = {
        ...log,
        date: timeAgo(log.createdAt),
        index: index + 1,
        id: log._id.slice(19, 24),
      };

      array.push(customerData);
    });

    setLogsObject({
      columns: columns,
      rows: array,
    });
  };

  const renderData = (obj, parentKey) => {
    try {
      if (obj) {
        return Object.keys(obj).map((key) => {
          const value = obj[key];
          const newKey = parentKey ? `${parentKey}.${key}` : key;

          if (typeof value === "object") {
            return renderData(value, newKey);
          } else {
            return (
              <div
                key={newKey}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: "row",
                  margin: "10px 0",
                  borderBottom: `1px solid ${COLORS.lightGrey}`,
                  paddingBottom: 10,
                  gap: 10,
                }}
              >
                <p id="subheader" style={{ textTransform: "capitalize" }}>
                  {newKey}:
                </p>
                <p id="subheaderBlack">{value}</p>
              </div>
            );
          }
        });
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const list = () => (
    <Box sx={{ width: 650, display: "flex", padding: 2 }} role="presentation">
      <div style={{ padding: 30 }}>
        <h2 style={{ margin: "0" }}>Log Details</h2>
        <p style={{ margin: "0 0 20px" }}>
          Analyze in detail what happened in this log
        </p>
        <div
          style={{
            border: `1px solid ${COLORS.lightGrey}`,
            padding: 30,
            borderRadius: 6,
            width: "540px",
            wordBreak: "break-all",
          }}
        >
          {Object.keys(log).map((key) => {
            return (
              <div key={key} style={{ margin: "10px 0" }}>
                {key === "function_specific_data" ? (
                  <div
                    style={{
                      background: COLORS.evenLighterGrey,
                      padding: 30,
                      borderRadius: 6,
                    }}
                  >
                    <h3 style={{ margin: "0 0 20px 0" }}>
                      Endpoint specific data
                    </h3>
                    {renderData(log[key])}
                  </div>
                ) : (
                  key !== "__v" && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        flexDirection: "row",
                        margin: "10px, 10px",
                        borderBottom: `1px solid #e0e0e0`,
                        paddingBottom: 10,
                        gap: 10,
                      }}
                    >
                      <p
                        id="subheader"
                        style={{
                          textTransform: "capitalize",
                          fontWeight: "100",
                          color: COLORS.grey,
                        }}
                      >
                        {key === "_id" ? "log ID" : key}
                      </p>
                      <p id="subheaderBlack" style={{ textAlign: "end" }}>
                        {key === "createdAt" || key === "updatedAt"
                          ? dateForHumans(log[key])
                          : log[key]}
                      </p>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Box>
  );

  const handleClick = (log) => {
    setLog(log);
    toggleDrawer();
  };

  return (
    <div>
      <Drawer anchor={"right"} open={openDrawer} onClose={() => toggleDrawer()}>
        {list("right")}
      </Drawer>
      <h1>Logs</h1>
      <p>
        Logs are recorded information about the use of your APIs. They are used
        to track the activity of your organization's users and to monitor the
        health of your APIs.
      </p>
      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <HashLoader color={COLORS.mainAccent} />
        </div>
      )}
      {logsObject && (
        <DataGrid
          {...logsObject}
          slots={{ toolbar: GridToolbar }}
          onCellClick={(e) => handleClick(e.row)}
          slotProps={{
            columnMenu: {
              background: "red",
              counter: logsObject.rows.length,
            },
          }}
          style={{
            fontFamily: "AuthenticSans90",
            marginBottom: "40px",
            marginTop: "20px",
          }}
          sx={{
            "& .MuiDataGrid-toolbarContainer": {
              "& .MuiButton-text": {
                color: COLORS.mainAccent,
                textTransform: "capitalize",
                fontFamily: "AuthenticSans90",
                fontSize: "14px",
              },
            },
          }}
        />
      )}
    </div>
  );
}
